export const COLOR_SCHEME_STORAGE_KEY = 'r2r-color-scheme-preference';
export const COLOR_SCHEME_PROFILE_KEY = 'colorScheme';

export const COLOR_SCHEME_PREFERENCES = ['system', 'light', 'dark'];
export const COLOR_SCHEMES = ['light', 'dark'];

export const COLOR_SCHEME_PAGE_BACKGROUNDS = {
  light: '#F0EBEB',
  dark: '#212437',
};

/** Matches `--r2rNavyBar` — used for mobile browser chrome above the sticky topbar. */
export const COLOR_SCHEME_TOPBAR_BACKGROUNDS = {
  light: '#101725',
  dark: '#101725',
};

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * @param {string} preference
 * @returns {boolean}
 */
export const isValidColorSchemePreference = preference =>
  COLOR_SCHEME_PREFERENCES.includes(preference);

/**
 * @param {string} scheme
 * @returns {boolean}
 */
export const isValidColorScheme = scheme => COLOR_SCHEMES.includes(scheme);

/**
 * Reads OS color scheme preference. Defaults to light when unavailable.
 *
 * @returns {'light' | 'dark'}
 */
export const getSystemColorScheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
  return 'light';
};

/**
 * Resolves a stored preference to an effective color scheme.
 *
 * @param {{ preference?: string }} options
 * @returns {'light' | 'dark'}
 */
export const resolveColorScheme = ({ preference = 'system' } = {}) => {
  const pref = isValidColorSchemePreference(preference) ? preference : 'system';
  if (pref === 'light') {
    return 'light';
  }
  if (pref === 'dark') {
    return 'dark';
  }
  return getSystemColorScheme();
};

/**
 * @param {import('../util/types').propTypes.currentUser} [currentUser]
 * @returns {string | null}
 */
export const getColorSchemePreferenceFromUser = currentUser => {
  const preference = currentUser?.attributes?.profile?.privateData?.[COLOR_SCHEME_PROFILE_KEY];
  return isValidColorSchemePreference(preference) ? preference : null;
};

/**
 * @param {{ currentUser?: object }} options
 * @returns {'system' | 'light' | 'dark'}
 */
export const resolveColorSchemePreference = ({ currentUser } = {}) => {
  const fromUser = getColorSchemePreferenceFromUser(currentUser);
  if (fromUser) {
    return fromUser;
  }

  const stored = readStoredColorSchemePreference();
  if (stored && isValidColorSchemePreference(stored)) {
    return stored;
  }

  return 'system';
};

/**
 * @returns {string | null}
 */
export const readStoredColorSchemePreference = () => {
  if (!isBrowser) {
    return null;
  }
  try {
    return window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * @param {string} preference
 */
export const writeStoredColorSchemePreference = preference => {
  if (!isBrowser || !isValidColorSchemePreference(preference)) {
    return;
  }
  try {
    window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
  } catch {
    /* ignore quota / privacy errors */
  }
};

export const clearStoredColorSchemePreference = () => {
  if (!isBrowser) {
    return;
  }
  try {
    window.localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * @param {'light' | 'dark'} scheme
 */
export const applyColorScheme = scheme => {
  const effectiveScheme = isValidColorScheme(scheme) ? scheme : 'light';
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.colorScheme = effectiveScheme;
  }
};
